import * as fs from 'fs';
import * as path from 'path';
import { fetchSurveyFromEnv } from './fetch-survey-inventory';
import { resolveProjectRoot } from './resolve-project-root';
import { colors, DEFAULT_DISTRIBUTION_DASHBOARD_URL } from './const';
import { getErrorMessage } from '../types/shared';
import {
  formatDistributionDoneHandoffBlocks,
  formatInput5HandoffBlocks,
} from './workflow-prompts';
import {
  assertDistributionDeployAllowed,
  recordDistributionS3Key,
} from './workflow-state';

const c = colors;
const MAX_FILES = 500;
const MAX_FILE_BYTES = 52_428_800;
const REPORT_MAX_ATTEMPTS = 3;
const REPORT_RETRY_DELAY_MS = 2000;

function requireEnv(name: string): string {
  const value = process.env[name]?.trim();
  if (!value) {
    throw new Error(`Missing ${name}`);
  }
  return value.replace(/\/+$/, '');
}

function resolveDashboardApi(): string {
  return (
    process.env.CFM_DASHBOARD_API?.trim() || DEFAULT_DISTRIBUTION_DASHBOARD_URL
  ).replace(/\/+$/, '');
}

function guessContentType(filePath: string): string {
  const ext = path.extname(filePath).toLowerCase();
  const map: Record<string, string> = {
    '.html': 'text/html',
    '.htm': 'text/html',
    '.css': 'text/css',
    '.js': 'application/javascript',
    '.mjs': 'application/javascript',
    '.json': 'application/json',
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.gif': 'image/gif',
    '.svg': 'image/svg+xml',
    '.webp': 'image/webp',
    '.ico': 'image/x-icon',
    '.woff': 'font/woff',
    '.woff2': 'font/woff2',
    '.ttf': 'font/ttf',
    '.txt': 'text/plain',
    '.xml': 'application/xml',
    '.map': 'application/json',
  };
  return map[ext] || 'application/octet-stream';
}

type DeployFile = { path: string; contentType: string; sizeBytes: number };

function buildFileManifest(outDir: string): DeployFile[] {
  const files: DeployFile[] = [];

  function walk(dir: string) {
    for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
      const full = path.join(dir, entry.name);
      if (entry.isDirectory()) {
        walk(full);
        continue;
      }
      const rel = path.relative(outDir, full).split(path.sep).join('/');
      const sizeBytes = fs.statSync(full).size;
      if (sizeBytes <= 0 || sizeBytes > MAX_FILE_BYTES) {
        throw new Error(`File size out of range: ${rel} (${sizeBytes} bytes)`);
      }
      files.push({
        path: rel,
        contentType: guessContentType(rel),
        sizeBytes,
      });
    }
  }

  walk(outDir);
  files.sort((a, b) => a.path.localeCompare(b.path));

  if (files.length === 0) {
    throw new Error(`No files found in ${outDir}`);
  }
  if (files.length > MAX_FILES) {
    throw new Error(`${files.length} files exceeds API limit of ${MAX_FILES}`);
  }
  return files;
}

type StartResponse = {
  deployId: string;
  s3Prefix: string;
  uploads: Array<{ path: string; uploadUrl: string }>;
};

type CompleteResponse = {
  url: string;
  deployId: string;
  surveyId: string;
  s3Prefix: string;
  distributionS3Key?: string;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function startDistributionUpload(
  deployApiBase: string,
  parentJwt: string,
  s3Key: string,
  files: DeployFile[],
): Promise<StartResponse> {
  const response = await fetch(`${deployApiBase}/deploy/start`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${parentJwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ files, distributionS3Key: s3Key }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      typeof body?.message === 'string' ? body.message : `Deploy API error (${response.status})`,
    );
  }
  return body as StartResponse;
}

async function uploadFiles(
  outDir: string,
  uploads: Array<{ path: string; uploadUrl: string }>,
  manifest: DeployFile[],
): Promise<void> {
  const mimeByPath = new Map(manifest.map((f) => [f.path, f.contentType]));
  const failed: string[] = [];

  for (const item of uploads) {
    const filePath = path.join(outDir, item.path);
    const contentType = mimeByPath.get(item.path) || 'application/octet-stream';
    const body = fs.readFileSync(filePath);

    const response = await fetch(item.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body,
    });

    if (!response.ok) {
      failed.push(item.path);
    }
  }

  if (failed.length > 0) {
    throw new Error(
      `${failed.length} upload(s) failed (${failed.slice(0, 3).join(', ')}${failed.length > 3 ? '…' : ''}) — rerun create-distribution (presigned URLs expire)`,
    );
  }
}

async function completeDistributionUpload(
  deployApiBase: string,
  parentJwt: string,
  deployId: string,
): Promise<CompleteResponse> {
  const response = await fetch(`${deployApiBase}/deploy/complete`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${parentJwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ deployId }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      typeof body?.message === 'string' ? body.message : `Deploy API error (${response.status})`,
    );
  }
  return body as CompleteResponse;
}

async function reportToDashboardOnce(
  dashboardApi: string,
  payload: Record<string, unknown>,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(`${dashboardApi}/api/distributions/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: typeof body?.message === 'string' ? body.message : `HTTP ${response.status}`,
      };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: getErrorMessage(err) };
  }
}

async function reportToDashboardWithRetries(
  dashboardApi: string,
  payload: Record<string, unknown>,
): Promise<void> {
  let lastError = 'unknown error';

  for (let attempt = 1; attempt <= REPORT_MAX_ATTEMPTS; attempt += 1) {
    const result = await reportToDashboardOnce(dashboardApi, payload);
    if (result.ok) {
      return;
    }
    lastError = result.error || lastError;
    if (attempt < REPORT_MAX_ATTEMPTS) {
      console.log(`  dashboard report attempt ${attempt} failed — retrying…`);
      await sleep(REPORT_RETRY_DELAY_MS);
    }
  }

  throw new Error(
    `Dashboard sync failed after ${REPORT_MAX_ATTEMPTS} attempts (${lastError}). Re-run: cfm-sdk create-distribution --s3-key=${payload.s3Key}`,
  );
}

/**
 * Upload the built static UI to a distribution path and sync status to the dashboard.
 *
 * The client generates the S3 key in the distribution dashboard; the agent uploads
 * OUT_DIR directly to sites/{surveyId}/distributions/{s3Key}/ and reports back.
 */
export async function runCreateDistribution(options: {
  projectRoot?: string;
  instanceIdOverride?: string;
  s3Key?: string;
}): Promise<number> {
  try {
    const projectRoot = options.projectRoot || resolveProjectRoot(process.cwd());
    const deployApiBase = requireEnv('CFM_DEPLOY_API_BASE');
    const outDir = process.env.OUT_DIR?.trim();

    const instanceId =
      options.instanceIdOverride?.trim() ||
      process.env.CFM_INSTANCE_ID?.trim() ||
      (() => {
        throw new Error('Set CFM_INSTANCE_ID or pass --instance-id=');
      })();

    const s3Key = options.s3Key?.trim();
    if (!s3Key) {
      let surveyId = '<survey-id>';
      try {
        const instanceId =
          options.instanceIdOverride?.trim() ||
          process.env.CFM_INSTANCE_ID?.trim();
        if (instanceId) {
          const { survey } = await fetchSurveyFromEnv(projectRoot, instanceId);
          surveyId = survey.id;
        }
      } catch {
        // fall through with placeholder
      }
      console.error(
        `\n${formatInput5HandoffBlocks(surveyId)}\n`,
      );
      return 1;
    }

    try {
      assertDistributionDeployAllowed(projectRoot, s3Key);
    } catch (err) {
      console.error(`${c.red}${getErrorMessage(err)}${c.reset}`);
      return 1;
    }

    if (!outDir) {
      throw new Error('Missing OUT_DIR — set OUT_DIR to the absolute path of out/ or dist/');
    }
    if (!fs.existsSync(path.join(outDir, 'index.html'))) {
      throw new Error(`Missing ${path.join(outDir, 'index.html')} — run build first`);
    }

    const { survey, apiResponse } = await fetchSurveyFromEnv(projectRoot, instanceId);
    const surveyId = survey.id;
    const surveyName = apiResponse.cfmSurveyDTO?.name?.trim() || surveyId;
    const dashboardApi = resolveDashboardApi();

    const manifest = buildFileManifest(outDir);

    console.log(
      `${c.cyan}→ Creating distribution${c.reset} for ${surveyName} (${surveyId}, s3Key: ${s3Key})`,
    );
    console.log(`  uploading ${manifest.length} files from ${outDir}…`);

    const started = await startDistributionUpload(deployApiBase, instanceId, s3Key, manifest);
    await uploadFiles(outDir, started.uploads, manifest);
    console.log(`  uploads complete — finalizing deploy…`);

    const completed = await completeDistributionUpload(
      deployApiBase,
      instanceId,
      started.deployId,
    );
    const liveUrl = completed.url;

    await reportToDashboardWithRetries(dashboardApi, {
      surveyId,
      surveyName,
      s3Key,
      status: 'deployed',
      liveUrl,
      jobId: started.deployId,
    });

    console.log(
      `${c.green}✓ Dashboard updated${c.reset} (status: deployed, live URL set): ${dashboardApi}`,
    );
    console.log(`${c.green}✓ Distribution deployed${c.reset}: ${s3Key}`);
    console.log(`\n${formatDistributionDoneHandoffBlocks(liveUrl)}\n`);
    recordDistributionS3Key(projectRoot, s3Key);
    return 0;
  } catch (err) {
    console.error(`${c.red}❌ create-distribution failed: ${getErrorMessage(err)}${c.reset}`);
    return 1;
  }
}
